/**
 * What happens after the wing stops flying, tier by tier (AD-007b):
 *
 * - **Novice** — nothing here runs; the stall is a warning only.
 * - **Intermediate** — the manual's *"slow spiral dive"*: bank without back
 *   pressure (or a stall) degenerates into a steepening, nose-dropping turn.
 * - **Expert** — autorotation, for the aircraft the data says can spin.
 *
 * Spin entry comes from `.DAT` offset 72, a 4-level per-type enum — 0 never
 * spins, 30 "spins very easily" (Camel, Dr.I, SSW D.III, Sopwith Triplane), 50
 * "fairly easily", 60 "only if provoked". Crucially this is *not* the rotary
 * set (flight-tunables.md §4.2), so entry keys off this field and never off
 * `isRotary`.
 *
 * Recovery is the classic input pair: opposite rudder plus forward stick, held.
 * The thresholds are CALIBRATION-PENDING; the requirement that wrong inputs do
 * not recover is not.
 *
 * `updateSpiral`'s coordinated-turn check (`loadFactor < required/cos φ`) did
 * not change under AD-007h. It reads `forces.ts`'s `loadFactor`, which is
 * purely the wing's AoA-driven lift over weight and was never coupled to the
 * (now-removed) lift-geometry turn cap — so a bank held without the back
 * pressure to hold `1/cos φ` g still degenerates into this module's spiral
 * exactly as before, and does so for the honest reason (insufficient lift for
 * the bank), not as a side effect of a turn-rate ceiling eating into the lift
 * the pilot asked for.
 */

import type { BodyRates } from "./math3d.ts";
import type { FlightInputs } from "./inputs.ts";
import type { SubsystemFlags } from "./tiers.ts";
import type { Tunables } from "./tunables.ts";
import { clamp, signOf } from "./units.ts";

export interface StallSpec {
  /** Raw `.DAT` offset 24 — 650 / 700 / 800 / 1000, higher = more lift, lower stall speed. */
  readonly liftCoefficientRaw: number;
  /** Raw `.DAT` offset 72 — 0 = will not spin, then 60 / 50 / 30 by increasing readiness. */
  readonly spinClassRaw: number;
}

export interface SpinState {
  readonly active: boolean;
  /** +1 spinning right, −1 left, 0 not spinning. */
  readonly direction: number;
  readonly recoverySeconds: number;
  readonly elapsedSeconds: number;
  /**
   * Seconds of grace after a recovery during which autorotation cannot restart.
   * Without it a successful recovery re-enters instantly, because breaking the
   * spin does not by itself unstall the wing — the pilot needs a window to fly
   * out of the stall the forward stick has only just started to cure.
   */
  readonly lockoutSeconds: number;
}

export interface SpiralState {
  readonly active: boolean;
  /** 0..1 — how far the spiral has developed. */
  readonly severity: number;
}

export interface StallModeState {
  readonly spin: SpinState;
  readonly spiral: SpiralState;
}

export interface StallModeUpdate {
  readonly stall: StallSpec;
  readonly flags: SubsystemFlags;
  readonly tunables: Tunables;
  readonly inputs: FlightInputs;
  readonly stalled: boolean;
  readonly alphaRad: number;
  readonly betaRad: number;
  /** True airspeed, m/s — what turns `betaRad` back into the skid it stands for. */
  readonly airspeedMps: number;
  /**
   * The far edge of the lift collapse, `alphaStallRad + postStallSpreadRad` —
   * past it `postStallFactor` has bottomed out and both wings are equally dead,
   * so there is no asymmetry left for autorotation to feed on.
   */
  readonly alphaCollapseBandRad: number;
  readonly bankRad: number;
  readonly loadFactor: number;
  readonly rates: BodyRates;
  readonly dt: number;
}

export interface StallModeResult {
  readonly state: StallModeState;
  /** Rate biases to add to the pilot's commands. */
  readonly rateBias: BodyRates;
  /** True while autorotation owns the aircraft and pilot rate commands are ignored. */
  readonly controlsOverridden: boolean;
}

export const NO_STALL_MODES: StallModeState = {
  spin: { active: false, direction: 0, recoverySeconds: 0, elapsedSeconds: 0, lockoutSeconds: 0 },
  spiral: { active: false, severity: 0 },
};

/** Sideslip needed to break this aircraft into a spin; ∞ for the types the data says never spin. */
export function spinEntryThresholdRad(stall: StallSpec, tunables: Tunables): number {
  if (stall.spinClassRaw <= 0) return Number.POSITIVE_INFINITY;
  return (tunables.spinEntryBetaRad * stall.spinClassRaw) / tunables.spinClassReference;
}

function recoveryInputsHeld(inputs: FlightInputs, direction: number, tunables: Tunables): boolean {
  const oppositeRudder =
    signOf(inputs.yaw) === -direction && Math.abs(inputs.yaw) >= tunables.spinRecoveryRudderInput;
  return oppositeRudder && inputs.pitch <= tunables.spinRecoveryStickInput;
}

function updateSpin(spin: SpinState, update: StallModeUpdate): SpinState {
  const { tunables } = update;
  if (!update.flags.spin) return NO_STALL_MODES.spin;

  if (!spin.active) {
    const lockoutSeconds = Math.max(0, spin.lockoutSeconds - update.dt);
    // The entry wants a *skid*, and a skid is a lateral speed. `betaRad` is an
    // angle, so its noise grows as 1/V and an aeroplane hanging on its
    // propeller at 4 mph reports 9–15° of sideslip out of a few centimetres per
    // second of drift. Requiring the lateral speed as well as the angle is what
    // keeps a stationary aeroplane from autorotating — see
    // `spinEntryMinSideslipMps`, and AD-007o for the zoom this was measured on.
    const sideslipMps = Math.abs(Math.sin(update.betaRad) * update.airspeedMps);
    // …and it wants a wing that is still *breaking*, not one that is long gone.
    // Autorotation is driven by one wing being further down the lift collapse
    // than the other, so it lives inside the decay band `aero.ts` already
    // describes — from the break to `postStallSpreadRad` past it, which is
    // where `postStallFactor` reaches its floor. Beyond that both wings are
    // equally dead, there is no differential left to roll the aeroplane, and
    // what the port had instead was a Camel tripping a "spin" at α = 147° while
    // sliding backwards down its own zoom, and an S.S.W. at 105°. Structural
    // rather than tunable: it reuses the two numbers the stall curve is already
    // made of instead of adding a third.
    const wingStillBreaking = Math.abs(update.alphaRad) <= update.alphaCollapseBandRad;
    const provoked =
      lockoutSeconds <= 0 &&
      update.stalled &&
      wingStillBreaking &&
      sideslipMps >= tunables.spinEntryMinSideslipMps &&
      Math.abs(update.betaRad) >= spinEntryThresholdRad(update.stall, tunables);
    if (!provoked) return { ...NO_STALL_MODES.spin, lockoutSeconds };
    // Sideslip is signed by where the flight path sits relative to the nose, so
    // a nose yawed right (the classic pro-spin rudder input) reads as negative
    // beta — autorotation follows the nose, hence the negation.
    return {
      active: true,
      direction: signOf(-update.betaRad),
      recoverySeconds: 0,
      elapsedSeconds: 0,
      lockoutSeconds: 0,
    };
  }

  const holding = recoveryInputsHeld(update.inputs, spin.direction, tunables);
  const recoverySeconds = holding
    ? spin.recoverySeconds + update.dt
    : Math.max(0, spin.recoverySeconds - update.dt);
  if (recoverySeconds >= tunables.spinRecoverySeconds) {
    return { ...NO_STALL_MODES.spin, lockoutSeconds: tunables.spinRecoveryLockoutSeconds };
  }
  return {
    active: true,
    direction: spin.direction,
    recoverySeconds,
    elapsedSeconds: spin.elapsedSeconds + update.dt,
    lockoutSeconds: 0,
  };
}

/**
 * The coordinated-turn requirement. A bank held without enough back pressure to
 * keep the vertical lift component up against `1/cos φ` starts winding into the
 * manual's slow spiral dive; a stall does the same thing at Intermediate, where
 * the spiral is the stall's consequence instead of a spin.
 */
function updateSpiral(spiral: SpiralState, update: StallModeUpdate, spinning: boolean): SpiralState {
  const { flags, tunables } = update;
  if ((!flags.coordinatedTurn && !flags.spiralDive) || spinning) return NO_STALL_MODES.spiral;

  const bankMagnitude = Math.abs(update.bankRad);
  const required = 1 / Math.max(Math.cos(bankMagnitude), 0.05);
  const uncoordinated =
    flags.coordinatedTurn &&
    bankMagnitude > tunables.spiralBankThresholdRad &&
    update.loadFactor < required * tunables.spiralLoadFactorMargin;
  const growing = uncoordinated || (flags.spiralDive && update.stalled);

  const rate = update.dt / Math.max(tunables.spiralOnsetSeconds, 1e-3);
  const severity = clamp(spiral.severity + (growing ? rate : -rate), 0, 1);
  return { active: severity > 0, severity };
}

function spinRateBias(spin: SpinState, update: StallModeUpdate): BodyRates {
  const { tunables } = update;
  return {
    roll: spin.direction * tunables.spinRollRateRadPerSec,
    pitch: tunables.spinAlphaHoldGain * (tunables.spinAlphaTargetRad - update.alphaRad),
    yaw: spin.direction * tunables.spinYawRateRadPerSec,
  };
}

function spiralRateBias(spiral: SpiralState, update: StallModeUpdate): BodyRates {
  const { tunables } = update;
  return {
    roll: signOf(update.bankRad) * tunables.spiralRollBiasRadPerSec * spiral.severity,
    pitch: -tunables.spiralPitchBiasRadPerSec * spiral.severity,
    yaw: 0,
  };
}

export function updateStallModes(state: StallModeState, update: StallModeUpdate): StallModeResult {
  const spin = updateSpin(state.spin, update);
  const spiral = updateSpiral(state.spiral, update, spin.active);
  if (spin.active) {
    return { state: { spin, spiral }, rateBias: spinRateBias(spin, update), controlsOverridden: true };
  }
  return {
    state: { spin, spiral },
    rateBias: spiral.severity > 0 ? spiralRateBias(spiral, update) : { roll: 0, pitch: 0, yaw: 0 },
    controlsOverridden: false,
  };
}
